import PropTypes from 'prop-types';
import styled from 'styled-components';
import { T_SHIRT_SIZES } from '../../constants';
import { FlexMixin } from '../../mixins/components/FlexMixin';
import CustomPropTypes from '../../util/custom-prop-types';
import { doNotForwardTransientProp } from '../../util/doNotForwardTransientProp';

/**
 * A component implementation of standard CSS Flexbox.
 * No CSS will be added/browser defaults will be used unless you set a prop.
 * All props are camel-cased CSS attributes. By default,
 * each `Flex` component uses a `div` -- if needed, you can use the styled-components
 * [`as`](https://www.styled-components.com/docs/api#as-polymorphic-prop) prop to change
 * the element type.
 *
 * **All `Flex` props support inline media query objects.**
 * @related ['Grid']
 * @since 5.0.0
 */

var Flex = /*#__PURE__*/styled('div').withConfig({
  shouldForwardProp: doNotForwardTransientProp(['display', 'order'])
}).withConfig({
  displayName: "Flex",
  componentId: "c11n-8-107-0__sc-n94bjd-0"
})(["", ""], FlexMixin);
Flex.displayName = 'Flex';
Flex.propTypes = {
  /** (flex containers) */
  display: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['flex', 'inline-flex', 'none'])),
  /** (flex containers) */
  flexDirection: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['row', 'row-reverse', 'column', 'column-reverse'])),
  /** (flex containers) */
  flexFlow: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** (flex containers) */
  flexWrap: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse'])),
  /** (flex containers) */
  justifyContent: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'])),
  /** (flex containers) */
  alignItems: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'baseline', 'stretch'])),
  /** (flex containers) */
  alignContent: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'stretch'])),
  /** (flex items) */
  flex: CustomPropTypes.inlineMediaQuery(PropTypes.string),
  /** (flex items) */
  order: CustomPropTypes.inlineMediaQuery(PropTypes.number),
  /** (flex items) */
  flexGrow: CustomPropTypes.inlineMediaQuery(PropTypes.number),
  /** (flex items) */
  flexShrink: CustomPropTypes.inlineMediaQuery(PropTypes.number),
  /** (flex items) */
  flexBasis: CustomPropTypes.inlineMediaQuery(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /** (flex items) */
  alignSelf: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['auto', 'flex-start', 'flex-end', 'center', 'baseline', 'stretch'])),
  /** (flex gap) */
  gap: CustomPropTypes.inlineMediaQuery(PropTypes.oneOfType([PropTypes.oneOf(Object.values(T_SHIRT_SIZES)), PropTypes.number])),
  /**
   * @deprecated Use inline media query objects
   */
  mediaQuery: CustomPropTypes.mediaQuery
};

/** @component */
export { Flex };